import { inject, Injectable } from '@angular/core';
import { AsyncHttpClient } from './async-http-client.service';
import { AppId, TechDocRole } from '../../model';

export type PropertyGroupType = 'NUMBER' | 'BOOLEAN' | 'TEXT' | 'SELECTION';

/** One distinct value inside a variant field, with how many nodes carry it. */
export interface GroupVariant {
  value: string | number | null;
  count: number;
}

/**
 * One shared field of a key-group: the canonical (majority) value, plus the full variant
 * breakdown when the group's nodes disagree (`variants` present only when >1 distinct value).
 */
export interface GroupField {
  value: string | number | null;
  variants?: GroupVariant[];
}

/** Where one node of the group lives: its owning definition and per-definition sortOrder. */
export interface PropertyGroupUsage {
  definitionId: AppId;
  definitionName: string;
  /** Root-first parent chain joined with " › ", ending with the definition itself. */
  path: string;
  propertyId: AppId;
  sortOrder: number;
}

/**
 * One DISTINCT value-tuple within a key-group, with the definitions carrying exactly it.
 * Rendered as `[values, usages]*` on the list page; >1 set means the group has drifted.
 */
export interface PropertyValueSet {
  name: string;
  type: PropertyGroupType;
  minValue?: number | null;
  maxValue?: number | null;
  options?: string | null;
  description?: string | null;
  count: number;
  usages: PropertyGroupUsage[];
}

/**
 * All AttributePropertyDefinition nodes sharing one propertyKey, system-wide — a derived,
 * transitional concept until the unique-key migration collapses each group to one node.
 */
export interface PropertyGroup {
  propertyKey: string;
  count: number;
  /** >1 distinct type → group is read-only until repaired via harmonize-type. */
  typeClash: boolean;
  name: GroupField;
  type: GroupField;
  minValue: GroupField;
  maxValue: GroupField;
  options: GroupField;
  description: GroupField;
  techDocRole: GroupField;
  usages: PropertyGroupUsage[];
  /** Distinct value-tuples with their carriers, majority first. */
  valueSets: PropertyValueSet[];
}

/** Fan-out update: written to EVERY node carrying the key. */
export interface PropertyGroupUpdate {
  name: string;
  type: PropertyGroupType;
  minValue: number | null;
  maxValue: number | null;
  options: string | null;
  description: string | null;
  techDocRole: TechDocRole | null;
}

@Injectable({ providedIn: 'root' })
export class AttributePropertyService {
  private readonly http = inject(AsyncHttpClient);
  private readonly prefix = 'api/admin/attribute-property';

  list = async () => this.http.get<PropertyGroup[]>(this.prefix);

  update = async (key: string, body: PropertyGroupUpdate) =>
    this.http.put<PropertyGroup>(`${this.prefix}/${key}`, body);
}
